import api from '~/services/api';
import { buildPromotionRequest } from './buildPromotionRequest';
import {
  ContractPostAddress,
  ContractPostCustomer,
  ContractPostEmergency,
  ContractPostEquipment,
  ContractPostFiles
} from './types';

function withOrgQuery(path: string, organizationId?: string): string {
  if (!organizationId) return path;
  const params = new URLSearchParams();
  params.set('organization_id', organizationId);
  return `${path}?${params.toString()}`;
}

class ContractService {
  async getContext(contract_id: number, organizationId?: string) {
    if (!api.v2) {
      throw new Error('API v2 não está disponível');
    }
    const url = withOrgQuery(`/Contracts/${contract_id}/context`, organizationId);
    const {
      data: { data }
    } = await api.v2.get(url);
    return data;
  }
  async getContractById(contract_id: number, organizationId?: string) {
    const url = withOrgQuery(`/Contracts/${contract_id}`, organizationId);
    const {
      data: { data }
    } = await api.get(url);
    return data;
  }
  async getContractByHashId(hashId: string, organizationId?: string) {
    if (!api.v2) {
      throw new Error('API v2 não está disponível');
    }
    const params = new URLSearchParams();
    if (organizationId) params.set('organization_id', organizationId);
    const query = params.toString();
    const url = query ? `/Contracts/${hashId}?${query}` : `/Contracts/${hashId}`;
    const {
      data: { data }
    } = await api.v2.get(url);
    return data;
  }
  async postCustomer(contract_id: string, body: ContractPostCustomer, organizationId?: string) {
    const payload = organizationId ? { ...body, organization_id: organizationId } : body;
    const { data } = await api.v2!.put(`/Contracts/${contract_id}/customer`, payload);
    return data;
  }
  async postInsuredItemAddress(contract_id: string, body: ContractPostAddress, organizationId?: string) {
    const payload = organizationId ? { ...body, organization_id: organizationId } : body;
    const { data } = await api.v2!.post(`/contracts/${contract_id}/insured_items/address`, payload);
    return data;
  }
  async putInsuredItem(contract_id: number, body: ContractPostEquipment, organizationId?: string) {
    const payload = organizationId ? { ...body, organization_id: organizationId } : body;
    const { data } = await api.v2!.put(`/contracts/${contract_id}/insured_items/`, payload);
    return data;
  }
  async getManufacturers() {
    const {
      data: { data }
    } = await api.v2!.get('/manufacturers');
    return data?.length > 0
      ? data
      : [
          {
            id: '1',
            name: 'Sem Marca'
          }
        ];
  }
  async postEmergency(contract_id: number, body: ContractPostEmergency, organizationId?: string) {
    const payload = organizationId ? { ...body, organization_id: organizationId } : body;
    const { data } = await api.v2!.post(`/contracts/${contract_id}/emergency_data`, payload);
    return data;
  }
  async getDownload(contract_id: string, organizationId?: string) {
    const url = withOrgQuery(`/Contracts/${contract_id}/download`, organizationId);
    const response = await api.v2!.get(url, { responseType: 'arraybuffer' });
    return response;
  }
  async postFiles(contract_id: string, body: ContractPostFiles, organizationId?: string) {
    const payload = organizationId ? { ...body, organization_id: organizationId } : body;
    const { data } = await api.v2!.post(`/contracts/${contract_id}/insured_items/documents`, payload);
    return data;
  }
  async getVoucher(contract_id: number, organizationId?: string) {
    const url = withOrgQuery(`/Contracts/${contract_id}/contract_items/voucher`, organizationId);
    const { data } = await api.v2!.get(url);
    return data;
  }
  /** Config por requisição para evitar cache HTTP em fluxos críticos (ex.: pagamento) */
  private static getNoCacheConfig() {
    return {
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' as const },
      params: { _: Date.now() }
    };
  }

  async getStepPlans(plan_id: string, organization_id?: string, sellerId?: string) {
    const queryParams = new URLSearchParams();

    if (organization_id) {
      queryParams.append('organization_id', organization_id);
    }

    if (sellerId) {
      queryParams.append('seller', sellerId);
    }

    const queryString = queryParams.toString();
    const url = `/Plans/${plan_id}/flow${queryString ? `?${queryString}` : ''}`;

    const { data } = await api.v2!.get(url, ContractService.getNoCacheConfig());
    return data;
  }
  async getStepPlansWithChannel(planCode: string, distributorCode: string, channel?: string) {
    const queryParams = new URLSearchParams();
    if (channel) {
      queryParams.set('channel', channel);
    }
    const queryString = queryParams.toString();
    const url = `/Plans/${planCode}/distributor/${distributorCode}/flow${queryString ? `?${queryString}` : ''}`;
    const { data } = await api.v2!.get(url, ContractService.getNoCacheConfig());
    return data;
  }
  async postContractPromotion(quote_id: string, payload?: any, organizationId?: string) {
    const requestData = buildPromotionRequest(quote_id, payload, organizationId);

    const { data } = await api.v2!.post('/Contracts/promotion', requestData);
    return data;
  }
}

export default new ContractService();
